import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { Lightbulb, FileText, TrendingUp, Send, Loader2 } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

const SubmitIdea = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    profit_loss_description: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.profit_loss_description.trim()) newErrors.profit_loss_description = 'Profit & Loss is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL}/ideas`, { idea: formData });
      setSuccess(true);
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      console.error(error);
      setErrors({ submit: 'Failed to submit idea' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen pt-24 pb-12 flex items-center justify-center relative overflow-hidden">
      {/* Dynamic Background */}
      <div className="absolute inset-0 bg-background">
        <div className="absolute top-1/4 left-1/4 w-[40%] h-[40%] bg-primary/10 rounded-full blur-[100px] animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[40%] h-[40%] bg-secondary/10 rounded-full blur-[100px] animate-float"></div>
      </div>

      <div className="relative w-full max-w-3xl mx-4">
        <div className="bg-surface/60 backdrop-blur-xl border border-white/5 rounded-3xl overflow-hidden shadow-2xl">

          {/* Header */}
          <div className="bg-white/5 p-8 border-b border-white/5 text-center">
            <h1 className="text-3xl md:text-4xl font-bold font-display text-white mb-2">Submit Your Idea</h1>
            <p className="text-slate-400">Share your vision with the world. We can't wait to see what you build.</p>
          </div>

          <div className="p-8 md:p-10">
            {success ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Send className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Idea Submitted Successfully!</h3>
                <p className="text-slate-400">Redirecting to home page...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">

                {/* Title */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-slate-300 ml-1">
                    <Lightbulb className="w-4 h-4 text-primary" />
                    Idea Title
                  </label>
                  <input
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className="w-full bg-background/50 border border-white/10 rounded-xl py-4 px-5 text-white placeholder:text-slate-600 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all font-display text-lg"
                    placeholder="e.g., The Next Big Thing"
                  />
                  {errors.title && <span className="text-xs text-red-400 ml-1">{errors.title}</span>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Description */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-300 ml-1">
                      <FileText className="w-4 h-4 text-secondary" />
                      Detailed Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={6}
                      className="w-full bg-background/50 border border-white/10 rounded-xl py-4 px-5 text-white placeholder:text-slate-600 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all resize-none"
                      placeholder="Describe your idea in detail..."
                    />
                    {errors.description && <span className="text-xs text-red-400 ml-1">{errors.description}</span>}
                  </div>

                  {/* Profit & Loss */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-300 ml-1">
                      <TrendingUp className="w-4 h-4 text-green-400" />
                      Profit & Loss Analysis
                    </label>
                    <textarea
                      name="profit_loss_description"
                      value={formData.profit_loss_description}
                      onChange={handleChange}
                      rows={6}
                      className="w-full bg-background/50 border border-white/10 rounded-xl py-4 px-5 text-white placeholder:text-slate-600 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all resize-none"
                      placeholder="Estimated costs, revenue streams, and financial viability..."
                    />
                    {errors.profit_loss_description && <span className="text-xs text-red-400 ml-1">{errors.profit_loss_description}</span>}
                  </div>
                </div>

                {errors.submit && (
                  <div className="bg-red-500/10 text-red-400 p-4 rounded-xl text-center text-sm">
                    {errors.submit}
                  </div>
                )}

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-white text-slate-900 font-bold py-4 rounded-xl hover:bg-slate-200 hover:scale-[1.01] active:scale-[0.99] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Launch Idea
                        <Send className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmitIdea;
